import {Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';
import {TeamMembership} from 'src/app/types/team-management.type';
import {HttpClient} from '@angular/common/http';
import {ApiClient} from './userway-api.service';
import {
  CreateAccountlessSubunitData,
  DeleteExistingUnitMemberData,
  GetChildUnitsByParentIdData,
  InviteTeamMemberDto,
  InviteUnitMemberData,
  NewUnitOwnerDto,
  PopulateUnitWithOwnerData,
  RemoveUnitTeamPictureData,
  ResendInviteToUnitMemberData,
  ResendTeamMemberInviteDto,
  SetAgencyMfaConfigurationData,
  TeamMfaConfigDto,
  UnitCreationDto,
  UnitUpdateDto,
  UpdateTeamNameDto,
  UpdateUnitInfoData,
  UpdateUnitMemberPermissionsData,
  UpdateUnitNameData,
  UpdateUnitTeamMemberPermissionsDto,
  UploadUnitTeamPictureData,
  UploadUnitTeamPictureDto,
} from '../../../userway/sdk/SSOApiClient';

@Injectable({
  providedIn: 'root',
})
export class TeamManagementApiService {
  constructor(
    private http: HttpClient,
    private apiClient: ApiClient,
  ) {}

  getTeamsMembership(): Observable<TeamMembership[]> {
    const request = this.apiClient.ssoApi.unitTeamController.getAllActiveUserMemberships();
    return this.apiClient.fetch(request).pipe(
      map((response: any) => {
        return response.payload?.membershipTeams;
      }),
    );
  }

  updateSubUnitInfo(
    parentIdentifier: string,
    childIdentifier: string,
    data: UnitUpdateDto,
  ): Observable<UpdateUnitInfoData> {
    const request = this.apiClient.ssoApi.unitController.updateUnitInfo(parentIdentifier, childIdentifier, data);
    return this.apiClient.fetch(request);
  }

  addOwnerToUnit(
    parentIdentifier: string,
    childIdentifier: string,
    data: NewUnitOwnerDto,
  ): Observable<PopulateUnitWithOwnerData> {
    const request = this.apiClient.ssoApi.unitController.populateUnitWithOwner(parentIdentifier, childIdentifier, data);
    return this.apiClient.fetch(request);
  }

  getUserUnits(targetId: string): Observable<GetChildUnitsByParentIdData> {
    const request = this.apiClient.ssoApi.unitController.getChildUnitsByParentId(targetId);
    return this.apiClient.fetch(request);
  }

  updateUser({data, accountId, memberId}) {
    const request = this.apiClient.ssoApi.unitTeamController.setUnitMemberAllowedSites(accountId, memberId, data);
    return this.apiClient.fetch(request);
  }

  createSubAccount(targetId: string, data: UnitCreationDto): Observable<CreateAccountlessSubunitData> {
    const request = this.apiClient.ssoApi.unitController.createAccountlessSubunit(targetId, data);
    return this.apiClient.fetch(request);
  }

  uploadUserPicture(targetId: string, data: UploadUnitTeamPictureDto): Observable<UploadUnitTeamPictureData> {
    const request = this.apiClient.ssoApi.unitTeamController.uploadUnitTeamPicture(targetId, data);
    return this.apiClient.fetch(request);
  }

  removeUserPicture(targetId: string): Observable<RemoveUnitTeamPictureData> {
    const request = this.apiClient.ssoApi.unitTeamController.removeUnitTeamPicture(targetId);
    return this.apiClient.fetch(request);
  }

  enableTwoFactor(targetId: string, data: TeamMfaConfigDto): Observable<SetAgencyMfaConfigurationData> {
    const request = this.apiClient.ssoApi.unitTeamController.setAgencyMfaConfiguration(targetId, data);
    return this.apiClient.fetch(request);
  }

  sendInvitation(targetId: string, data: InviteTeamMemberDto): Observable<InviteUnitMemberData> {
    const request = this.apiClient.ssoApi.unitTeamController.inviteUnitMember(targetId, data);
    return this.apiClient.fetch(request);
  }

  changeUserPermissions(
    targetId: string,
    data: UpdateUnitTeamMemberPermissionsDto,
  ): Observable<UpdateUnitMemberPermissionsData> {
    const request = this.apiClient.ssoApi.unitTeamController.updateUnitMemberPermissions(targetId, data);
    return this.apiClient.fetch(request);
  }

  updateTeamName(targetId: string, data: UpdateTeamNameDto): Observable<UpdateUnitNameData> {
    const request = this.apiClient.ssoApi.unitController.updateUnitName(targetId, data);
    return this.apiClient.fetch(request);
  }

  resendInvitation(targetId: string, data: ResendTeamMemberInviteDto): Observable<ResendInviteToUnitMemberData> {
    const request = this.apiClient.ssoApi.unitTeamController.resendInviteToUnitMember(targetId, data);
    return this.apiClient.fetch(request);
  }

  removeUser(targetId: string, memberId: string): Observable<DeleteExistingUnitMemberData> {
    const request = this.apiClient.ssoApi.unitTeamController.deleteExistingUnitMember(targetId, memberId);
    return this.apiClient.fetch(request);
  }

  updateTeamMember({targetLink, data, memberId}) {
    const url = `${targetLink}/team/allow-sites/${memberId}`;
    return this.http.post(url, data);
  }
}
